import { useCallback, useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import {
  fetchModelPrediction,
  studyKeys,
  type ModelPredictionResponse,
  type ReaderStudyItem,
} from "@/hooks/useStudyQueries";
import { isNeuroModel, summarizeNeuroResult, type NeuroWorklistSummary } from "@/types/neuroReview";
import { readerCaseId } from "@/utils/readerCaseId";
import { isModelFailed, isModelRunning } from "@/utils/inferenceStatus";

export function useNeuroWorklist(
  token: string | null,
  userId: string | null,
  studies: readonly ReaderStudyItem[] | undefined,
): Record<string, NeuroWorklistSummary> {
  const neuroStudies = useMemo(
    () => studies?.filter((s) => isNeuroModel(s.model)) ?? [],
    [studies],
  );
  const combine = useCallback(
    (results: UseQueryResult<ModelPredictionResponse | null>[]) =>
      Object.fromEntries(
        neuroStudies.map((study, index) => {
          const query = results[index];
          let summary: NeuroWorklistSummary;
          if (isModelRunning(study.status)) {
            summary = { label: "Findings pending", findings: [], count: null };
          } else if (isModelFailed(study.status)) {
            summary = { label: "Prediction errored", findings: [], count: null };
          } else if (query.isPending) {
            summary = { label: "Results loading", findings: [], count: null };
          } else {
            summary = summarizeNeuroResult(query.isError ? undefined : query.data?.neuro_review);
          }
          return [readerCaseId(study), summary];
        }),
      ),
    [neuroStudies],
  );
  return useQueries({
    queries: neuroStudies.map((study) => ({
      queryKey: studyKeys.studyModelPrediction(userId || "", readerCaseId(study)),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchModelPrediction(token!, userId!, readerCaseId(study), signal),
      enabled: !!token && !!userId && !isModelRunning(study.status) && !isModelFailed(study.status),
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60,
    })),
    combine,
  });
}
